import {
  Action,
  Selector,
  State,
  StateContext,
} from "@ngxs/store";
import { map, catchError } from "rxjs/operators";
import {
  faceTimeOut,
  isCameraOpen,
  requestFaceImageQuality,
  setDefaultFace,
  setPadDisplay,
  setFaceImageLoad,
  getFaceMatchResult,
  setMatchLoading,
  setMatchLoaded,
  setCameraError
} from "../actions/face.action";
import { FaceService } from "../../services/async/connectors/face.service";
import { Injectable } from "@angular/core";
import { EMPTY } from "rxjs";
import { FaceStateModel, ErrorType } from "../../model/face.model";
@State<FaceStateModel>({
  name: "face",
  defaults: {
    faceImage: null,
    matchResult: null,
    matchLoading: false,
    matchLoaded: false,
    isLoaded: false,
    isLoading: true,
    isError: false,
    isCameraOpen: false,
    isPadDisplayed: false,
    isFaceImageLoading: false,
    timeOut: false,
    errorType: null,
  },
})
@Injectable({
  providedIn: "root",
})
export class FaceState {
  constructor(private faceService: FaceService) { }
  @Selector()
  static getFaceState(state: FaceStateModel) {
    return state;
  }
  @Selector()
  static getFace(state: FaceStateModel) {
    return state.faceImage;
  }
  @Selector()
  static getMatchResult(state: FaceStateModel) {
    return state.matchResult;
  }
  @Selector()
  static isMatchLoading(state: FaceStateModel) {
    return state.matchLoading;
  }
  @Selector()
  static isMatchLoaded(state: FaceStateModel) {
    return state.matchLoaded;
  }
  @Selector()
  static faceTimeOut(state: FaceStateModel) {
    return state.timeOut;
  }
  @Selector()
  static isLoaded(state: FaceStateModel) {
    return state.isLoaded;
  }
  @Selector()
  static isStateless(state: FaceStateModel) {
    return state.isLoading;
  }
  @Selector()
  static isError(state: FaceStateModel) {
    return state.isError;
  }
  @Selector()
  static isUnSufficiantQualityError(state: FaceStateModel) {
    return state.errorType === ErrorType.UNSUFFICIANT_QUALITY;
  }
  @Selector()
  static isNoFaceDetectedError(state: FaceStateModel) {
    return state.errorType === ErrorType.FACE_NOT_DETECTED;
  }
  @Selector()
  static isInvalidMediaTypeError(state: FaceStateModel) {
    return state.errorType === ErrorType.INVALID_MEDIA_TYPE;
  }
  @Selector()
  static isCameraOpen(state: FaceStateModel) {
    return state.isCameraOpen;
  }
  @Selector()
  static isPadDisplayed(state: FaceStateModel) {
    return state.isPadDisplayed;
  }
  @Selector()
  static isFaceImageLoading(state: FaceStateModel) {
    return state.isFaceImageLoading;
  }

  // Camera errors
  @Selector()
  static isCameraError(state: FaceStateModel) {
    return state.errorType === ErrorType.NO_CAMERA_FOUND
      || state.errorType === ErrorType.PERMISSION_DENIED
      || state.errorType === ErrorType.CAMERA_NOT_READABLE
      || state.errorType === ErrorType.CAMERA_ABORT;
  }
  @Selector()
  static isNoCameraFound(state: FaceStateModel) {
    return state.isError && state.errorType === ErrorType.NO_CAMERA_FOUND;
  }
  @Selector()
  static isPermissionDenied(state: FaceStateModel) {
    return state.isError && state.errorType === ErrorType.PERMISSION_DENIED;
  }
  @Selector()
  static isCameraNotReadable(state: FaceStateModel) {
    return state.isError && state.errorType === ErrorType.CAMERA_NOT_READABLE;
  }
  @Selector()
  static isAbortError(state: FaceStateModel) {
    return state.isError && state.errorType === ErrorType.CAMERA_ABORT;
  }

  @Action(setDefaultFace)
  setDefaultFace({ getState, patchState }: StateContext<FaceStateModel>) {
    const state = getState();
    patchState({
      faceImage: null,
      matchResult: false,
      matchLoading: false,
      matchLoaded: false,
      isLoaded: false,
      isLoading: true,
      isError: false,
      isCameraOpen: false,
      isPadDisplayed: false,
      isFaceImageLoading: false,
      timeOut: false,
    });
  }
  @Action(requestFaceImageQuality)
  getQuality(
    { getState, patchState }: StateContext<FaceStateModel>,
    { payload }: requestFaceImageQuality
  ) {
    return this.faceService.checkQuality(payload).pipe(
      map((result) => {
        const state = getState();
        if (result) {
          if (result.isQualityEnough) {
            const reader = new FileReader();

            reader.addEventListener(
              "load",
              function () {
                // convert image file to base64 string
                let value: string = reader.result.toString();
                let img = value.split(";base64,");

                patchState({
                  ...state,
                  faceImage: img[1],
                  isLoaded: true,
                  isLoading: false,
                  isFaceImageLoading: false,
                  isError: false,
                });
              },
              false
            );
            if (payload.file) {
              reader.readAsDataURL(payload.file);
            }

          } else {
            patchState({
              ...state,
              isLoaded: false,
              isLoading: false,
              isFaceImageLoading: false,
              isError: true,
              errorType: ErrorType.UNSUFFICIANT_QUALITY,
            });
          }
        }
      }),
      catchError((error) => {
        const state = getState();
        if (error.status === 0 || error.status === 401) {
          patchState({
            ...state,
            isLoaded: false,
            isLoading: true,
            isFaceImageLoading: false,
            timeOut: true,
          });
          return EMPTY;
        } else if (error.status === ErrorType.FACE_NOT_DETECTED) {
          patchState({
            ...state,
            isLoaded: false,
            isLoading: false,
            isFaceImageLoading: false,
            isError: true,
            errorType: ErrorType.FACE_NOT_DETECTED,
            timeOut: false,
          });
        }
        else if (error.status === ErrorType.INVALID_MEDIA_TYPE) {
          patchState({
            ...state,
            isLoaded: false,
            isLoading: false,
            isFaceImageLoading: false,
            isError: true,
            errorType: ErrorType.INVALID_MEDIA_TYPE,
            timeOut: false,
          });
        }
      })
    );
  }

  @Action(getFaceMatchResult)
  getFaceMatchResult(
    { getState, patchState }: StateContext<FaceStateModel>,
    { payload }: getFaceMatchResult
  ) {

    return this.faceService.matchPad(payload.pvtCode, payload.idFile, payload.token, payload.url).subscribe(
      (match) => {
        const state = getState();
        patchState({
          ...state,
          faceImage: payload.faceImage,
          matchResult: match.decision,
          matchLoading: false,
          matchLoaded: true
        })
      }
    );

  }

  @Action(setMatchLoading)
  setMatchLoading(
    { getState, patchState }: StateContext<FaceStateModel>,
    { payload }: setMatchLoading
  ) {
    const state = getState();
    patchState({
      ...state,
      matchLoading: payload
    });
  }
  @Action(setMatchLoaded)
  setMatchLoaded(
    { getState, patchState }: StateContext<FaceStateModel>,
    { payload }: setMatchLoaded
  ) {
    const state = getState();
    patchState({
      ...state,
      matchLoaded: payload
    });
  }

  @Action(isCameraOpen)
  setCameraPosition(
    { getState, patchState }: StateContext<FaceStateModel>,
    { payload }: isCameraOpen
  ) {
    const state = getState();
    patchState({ ...state, isCameraOpen: payload });
  }
  @Action(setPadDisplay)
  setPadDisplay(
    { getState, patchState }: StateContext<FaceStateModel>,
    { payload }: setPadDisplay
  ) {
    const state = getState();
    patchState({
      ...state,
      isPadDisplayed: payload,
    });
  }
  @Action(setFaceImageLoad)
  setFaceImageLoad(
    { getState, patchState }: StateContext<FaceStateModel>,
    { payload }: setFaceImageLoad
  ) {
    const state = getState();
    patchState({
      ...state,
      isFaceImageLoading: payload,
    });
  }
  @Action(faceTimeOut)
  setFaceTimeOut(
    { getState, patchState }: StateContext<FaceStateModel>,
    { payload }: faceTimeOut
  ) {
    const state = getState();
    patchState({ ...state, timeOut: payload });
  }

  @Action(setCameraError)
  setCameraError(
    { patchState }: StateContext<FaceStateModel>,
    { payload }: setCameraError
  ) {
    patchState({
      isError: payload.isError,
      errorType: payload.errorType
    })
  }
}
